import { moveItemInArray } from '@angular/cdk/drag-drop';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

export abstract class BaseCacheService<T> {
  protected dataSubject = new BehaviorSubject<any>(null);
  private isFetching = false; // Prevent multiple simultaneous fetches
  
  public data$: Observable<any> = this.dataSubject.asObservable().pipe(
    filter(data => data !== null)
  );

  // Abstract method for fetching data from the API
  protected abstract fetchApiData(): Observable<any>;

  // Fetch data if not already fetched
  fetchData(): void {
    if (this.dataSubject.getValue() === null && !this.isFetching) {
      this.isFetching = true;
      this.fetchApiData().subscribe(response => {
        this.dataSubject.next(response?.data ? response?.data : response);
        this.isFetching = false;
      });
    }
  }

  // Private method to ensure cache is loaded
  private async ensureCacheLoaded(): Promise<void> {
    if (this.dataSubject.getValue() === null && !this.isFetching) {
      this.isFetching = true;
      try {
        const response = await firstValueFrom(this.fetchApiData());
        this.dataSubject.next(response?.data ? response?.data : response);
      } finally {
        this.isFetching = false;
      }
    }
    // Wait if another fetch is in progress
    while (this.isFetching) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  // Create: Adds a new item to the cache
  async createLocal(newItem: any): Promise<void> {
    // Ensure cache is loaded before creating
    await this.ensureCacheLoaded();
    
    const currentData = this.dataSubject.getValue() || [];
    const exists = currentData.some(item => item.id === newItem.id);
    
    if (!exists) {
      this.dataSubject.next([...currentData, newItem]);
    }
  }

  // Update: Replaces an existing item in the cache
  async updateLocal(updatedItem: T, id: number): Promise<void> {
    // Ensure cache is loaded before updating
    await this.ensureCacheLoaded();
    
    const currentData = this.dataSubject.getValue() || [];
    if(currentData.length > 0){
      const updatedData = currentData.map(item =>
        (item as any).id === id ? updatedItem : item
      );
      this.dataSubject.next(updatedData);
    }
  }

  // Delete: Removes an item from the cache
  async deleteLocal(id: number): Promise<void> {
    // Ensure cache is loaded before deleting
    await this.ensureCacheLoaded();
    
    const currentData = this.dataSubject.getValue() || [];
    if(currentData.length > 0){
      const updatedData = currentData.filter(item => (item as any).id !== id);
      this.dataSubject.next(updatedData);
    }
  }

  // Sequence update for drag and drop
  async sequenceUpdate(previousIndex: number, currentIndex: number): Promise<void> {
    // Check if cache is already loaded
    const isAlreadyLoaded = this.dataSubject.getValue() !== null;
    
    // Ensure cache is loaded
    await this.ensureCacheLoaded();
    
    // If cache wasn't loaded before, the data from API is already in correct order
    if (!isAlreadyLoaded) {
      return;
    }
    
    // Only rearrange if cache was already loaded
    const currentData: T[] = this.dataSubject.getValue() || [];
  
    if (currentData.length > 0) {
      moveItemInArray(currentData, previousIndex, currentIndex);
      
      
      // Emit a new array reference
      const newData = [...currentData];
      this.dataSubject.next(newData);
    } else {
      console.warn('[Cache] sequenceUpdate called but cache is empty.');
    }
  }

  // Helper method to check if cache is initialized
  isCacheInitialized(): boolean {
    return this.dataSubject.getValue() !== null;
  }

  // Optional: Clear cache method
  clearCache(): void {
    this.dataSubject.next(null);
  }
}